import { EventEmitter, Injectable, Output } from '@angular/core';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { environment } from 'src/environments/environment';
import { StatePersistenceService } from './state-persistence.service';
import { SCORM_PENDING_EVENTS_STORAGE_PREFIX } from './scorm-storage.constants';
import { from, map, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private manager = new UserManager(this.getClientSettings());
  public User: User | null = null;
  public Name!: string;
  public currentUrl!: string;

  constructor(private statePersistenceService: StatePersistenceService) {
    this.manager.getUser().then((user) => {
      if (user != null) {
        this.User = user;
        if (this.User.profile.name == null)
          this.Name = localStorage.getItem('name') ?? "";
        else
          this.Name = this.User.profile.given_name + " " + this.User.profile.family_name;
      }
    });
  }

  private clearStorageExceptPendingEvents(): void {
    sessionStorage.clear();

    const keysToRemove: string[] = [];

    for (let index = 0; index < localStorage.length; index++) {
      const key = localStorage.key(index);

      if (key && !key.startsWith(SCORM_PENDING_EVENTS_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  @Output() getLoggedInName: EventEmitter<any> = new EventEmitter();

  getClientSettings(): UserManagerSettings {
    return {
      authority: environment.SSO_ConnexionPage_Uri,
      client_id: 'LMS',
      redirect_uri: environment.SSO_Redirect_Uri,
      post_logout_redirect_uri: environment.SSO_Post_Logout_Redirect_Uri,
      silent_redirect_uri: environment.SSO_Silent_Redirect_Uri,
      response_type: 'code',
      scope: 'openid profile offline_access',
      filterProtocolClaims: true,
      loadUserInfo: true,
      monitorSession: false,
      automaticSilentRenew: true,
      accessTokenExpiringNotificationTimeInSeconds: 120,
    };
  }

  completeSilentAuthentication(): Promise<void> {
    return this.manager.signinSilentCallback().then(() => { });
  }

  async checkAuthentication(): Promise<boolean> {
    if (this.User == null) {
      this.User = await this.manager.getUser();
    }

    if (this.User != null && !this.User.expired) {
      return true;
    }

    if (this.User != null && this.User.expired) {
      try {
        this.User = await this.manager.signinSilent();

        return this.User != null && !this.User.expired;
      } catch {
        return false;
      }
    }

    return false;
  }

  isLoggedIn(): boolean {
    return this.User != null && !this.User.expired;
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then((user) => {
      this.statePersistenceService.clearAllStates();

      this.User = user;
      this.getLoggedInName.emit(user.profile);
      this.Name = user.profile.given_name + " " + user.profile.family_name;
      localStorage.setItem('name', user.profile.name ?? "");
    })
      .catch((err) => { console.log(err) })
      .finally(() => { });
  };

  logout() {
    this.manager.signoutRedirect();
    this.clearStorageExceptPendingEvents();
  }

  logoutToLogin() {
    const loginUrl = environment.SSO_ConnexionPage_Uri + 'Account/Login';
    this.manager.signoutRedirect({
      post_logout_redirect_uri: loginUrl,
      id_token_hint: this.User?.id_token
    });
    this.clearStorageExceptPendingEvents();
  }


  refreshToken(): Observable<string> {
    return from(this.manager.signinSilent()).pipe(
      map(user => {
        if (user == null) {
          throw new Error('Le rafraichissement silencieux du token a echoue.');
        }

        this.User = user;

        return user.access_token;
      })
    );
  }
}
